import React from 'react';
import { useParams, useRoutes } from 'react-router-dom';
import { Box, HeaderPage } from '@backstage/ui';
import { Content } from '@backstage/core-components';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import {
  TechDocsReaderPageProvider,
  useTechDocsReaderPage,
} from '@backstage/plugin-techdocs-react';
import {
  TechDocsReaderPageContent,
  TechDocsReaderPageSubheader,
} from '@backstage/plugin-techdocs';
import { CookieAuthRefreshProvider } from '@backstage/plugin-auth-react';
import { appTranslationRef } from '../../core/i18n';
import styles from './techdocs-reader.page.module.css';

const TechDocsReaderInner = () => {
  const { t } = useTranslationRef(appTranslationRef);
  const { title, entityMetadata } = useTechDocsReaderPage();
  const { namespace = 'default', kind = 'component', name = '' } = useParams();

  const entityTitle = entityMetadata?.value?.metadata?.title || name;

  return (
    <Box className={styles.pageShell}>
      <HeaderPage
        title={title || name}
        breadcrumbs={[
          { label: t('docs.pageTitle'), href: '/docs' },
          { label: entityTitle, href: `/catalog/${namespace}/${kind}/${name}` },
        ]}
      />
      <TechDocsReaderPageSubheader />
      <Content stretch>
        <TechDocsReaderPageContent withSearch />
      </Content>
    </Box>
  );
};

const TechDocsReaderPageShell = () => {
  const {
    namespace = 'default',
    kind = 'component',
    name = '',
  } = useParams();

  return (
    <CookieAuthRefreshProvider pluginId="techdocs">
      <TechDocsReaderPageProvider entityRef={{ namespace, kind, name }}>
        <TechDocsReaderInner />
      </TechDocsReaderPageProvider>
    </CookieAuthRefreshProvider>
  );
};

/**
 * Wraps the custom reader page in a nested route structure so that
 * TechDocs addons (e.g. Mermaid) are discoverable via useOutlet()
 * by useTechDocsAddons() inside TechDocsReaderPageContent.
 */
export const TechDocsReaderPage = ({
  addons,
}: {
  addons?: React.ReactNode;
}) => {
  const element = useRoutes([
    {
      path: '*',
      element: <TechDocsReaderPageShell />,
      children: [{ path: '*', element: addons ?? null }],
    },
  ]);
  return element;
};
